import React, { useRef, useState } from "react";

import { Button, Checkbox, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { WorkspaceColumnSettingsIcon } from "assets/material-custom-icons";

interface Props {
  measureColumns: string[];
  columnVisibility: Record<string, boolean>;
  onToggle: (columnId: string) => void;
  onToggleAll: (allVisible: boolean) => void;
}

export const SetColumnsButton: React.FC<Props> = ({ measureColumns, columnVisibility, onToggle, onToggleAll }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const allVisible = measureColumns.every((col) => columnVisibility[col] !== false);
  const someVisible = !allVisible && measureColumns.some((col) => columnVisibility[col] !== false);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<WorkspaceColumnSettingsIcon sx={{ fontSize: 24, fill: "#0A3142" }} />}
        ref={buttonRef}
        onClick={() => setAnchorEl(buttonRef.current)}
        size="sm"
        sx={{
          textTransform: "none",
          whiteSpace: "nowrap",
          bgcolor: "#E6E9EB",
          borderColor: "#E6E9EB",
          minWidth: 115,
          height: "26px",
          "&:hover": { borderColor: "#0A3142", bgcolor: "#E6E9EB" }
        }}
      >
        Set Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow:
                "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.10), 0px 1px 8px 0px rgba(38,54,70,0.08)",
              py: "8px",
              minWidth: 220,
              maxHeight: 400,
              overflowY: "auto"
            }
          }
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {/* All */}
        <MenuItem
          onClick={() => onToggleAll(!allVisible)}
          sx={{ display: "flex", gap: "8px", alignItems: "center", px: "12px", py: "8px", minHeight: 40, "&:hover": { backgroundColor: "#ECF1F8" } }}
        >
          <Checkbox
            checked={allVisible}
            indeterminate={someVisible}
            size="medium"
            disableRipple
            readOnly
            sx={{ p: 0, color: "#0a3142", "&.Mui-checked": { color: "#0a3142" }, "&.MuiCheckbox-indeterminate": { color: "#0a3142" } }}
          />
          <Typography sx={{ fontSize: 16, color: "#071f35", lineHeight: "24px", fontWeight: 600 }}>
            All
          </Typography>
        </MenuItem>
        <Divider sx={{ my: "4px !important" }} />
        {measureColumns.map((col) => (
          <MenuItem
            key={col}
            onClick={() => onToggle(col)}
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              px: "12px",
              py: "8px",
              minHeight: 40,
              "&:hover": { backgroundColor: "#ECF1F8" }
            }}
          >
            <Checkbox
              checked={columnVisibility[col] !== false}
              size="medium"
              disableRipple
              readOnly
              sx={{ p: 0, color: "#0a3142", "&.Mui-checked": { color: "#0a3142" } }}
            />
            <Typography sx={{ fontSize: 16, color: "#071f35", lineHeight: "24px", fontWeight: 400 }}>
              {col}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
